<script setup lang="ts">
import { nextTick, onUnmounted, shallowRef, watch } from "vue";
import { useEventsBinding } from "../composable/events-binding";
import { useMap } from "../composable/google-map";
import { useReactiveMarkerPosition } from "../composable/marker-position";
import type { MarkerOptions } from "../composable/markers";
import { useLib } from "../lib-loader";

const markerEvents = ["click", "drag", "dragend", "dragstart", "mouseover", "mouseout"];

const props = defineProps<MarkerOptions>();
const emit = defineEmits<{
	click: [];
	drag: [google.maps.MapMouseEvent];
	dragend: [google.maps.MapMouseEvent];
	dragstart: [google.maps.MapMouseEvent];
	mouseover: [];
	mouseout: [];
	"update:position": [google.maps.LatLng | google.maps.LatLngLiteral];
}>();

defineSlots<{
	default(slotProps: { position: google.maps.LatLng | google.maps.LatLngLiteral }): void;
}>();

const marker = shallowRef<google.maps.Marker | null>(null);

const markerPosition = useReactiveMarkerPosition(marker);
watch(markerPosition, (pos) => {
	if (pos != null) {
		emit("update:position", pos);
	}
});

const { bindEvents: bindMarkerEvents } = useEventsBinding(markerEvents);

const map = useMap();

function removeMarker() {
	if (marker.value) {
		marker.value.setMap(null);
	}

	marker.value = null;
}

const markerLib = useLib("marker");

const stopInit = watch(
	[map, markerLib],
	() => {
		if (!map.value || !markerLib.value) return;

		marker.value = new markerLib.value.Marker({
			map: map.value,
			...props,
		});

		bindMarkerEvents(marker.value);

		nextTick(() => stopInit());
	},
	{ immediate: true }
);

watch(
	() => props.position,
	(position) => {
		markerPosition.value = position;
	}
);

watch(
	() => props.icon,
	(icon) => {
		marker.value?.setIcon(icon);
	}
);

watch(
	() => props.draggable,
	(draggable) => {
		if (marker.value == null) return;

		marker.value.setDraggable(!!draggable);
	}
);

watch(
	() => props.clickable,
	(clickable) => {
		if (marker.value == null) return;

		marker.value.setClickable(!!clickable);
	}
);

watch(
	() => props.title,
	() => {
		if (marker.value == null) {
			return;
		}

		marker.value.setTitle(props.title);
	}
);

onUnmounted(removeMarker);
</script>

<template>
	<slot v-if="marker && marker.getMap() != null" :position="marker.getPosition()!" />
</template>
